/**
 * SoleadLab 2.0 — Site Truth Qualification Types
 *
 * TypeScript interfaces for the truth qualification API responses.
 * Maps to the site_truth_qualifications table and API endpoints.
 */

// ─── Core types ──────────────────────────────────────────────────────────────

export type ConclusionFamily = 'performance' | 'condition' | 'risk';
export type ScopeType = 'whole_site' | 'structural_subset';
export type EligibilityState = 'blocked' | 'qualified' | 'defensible';
export type EvidenceStatus = 'none' | 'weak' | 'adequate' | 'strong';
export type FreshnessStatus = 'fresh' | 'aging' | 'stale' | 'expired';

// ─── Truth Qualification Row ─────────────────────────────────────────────────

export interface SiteTruthRow {
  id: string;
  site_id: string;
  conclusion_family: ConclusionFamily;
  scope_type: ScopeType;
  scope_definition_json: string | null;
  scope_hash: string;
  reference_basis: string;

  completeness_score: number;
  coverage_score: number;
  confidence_score: number;

  eligibility_state: EligibilityState;
  blocking_reasons_json: string;

  direct_evidence_status: EvidenceStatus;
  freshness_status: FreshnessStatus;
  multimodality_ok: number; // 0 or 1

  evidence_summary_json: string;
  coverage_breakdown_json: string;
  missing_json: string;
  explanation_json: string;

  topology_hash: string | null;
  input_hash: string;
  engine_version: string;
  policy_version: string;
  computed_at: string;
  created_at: string;
}

// ─── Parsed JSON shapes ──────────────────────────────────────────────────────

export interface TruthExplanation {
  summary: string;
  reference_basis_text: string;
  limitations: string[];
  next_actions: string[];
}

export interface TruthMissing {
  family: string;
  asset_type: string;
  count: number;
  suggestion: string;
}

export interface TruthEvidenceSummary {
  denominator: number;
  counted_families: string[];
  per_family_stats: Record<string, { asset_count: number; freshness: string }>;
}

export interface TruthCoverageBreakdown {
  by_class: Array<{ asset_type: string; total: number; covered: number; coverage: number }>;
  freshness_adjustments: Array<{ asset_type: string; penalty: number }>;
  critical_subsystems: Array<{ asset_type: string; covered: boolean }>;
}

// ─── API Response shapes ─────────────────────────────────────────────────────

export interface TruthRecomputeResultItem {
  family: string;
  outcome: string;
  truth_qualification?: {
    id: string;
    eligibility_state: EligibilityState;
    completeness_score: number;
    coverage_score: number;
    confidence_score: number;
    created: boolean;
  };
  error?: string;
}

export interface TruthRecomputeResponse {
  results: TruthRecomputeResultItem[];
}

export interface TruthHistoryResponse {
  items: SiteTruthRow[];
  total: number;
}

// ─── Labels ──────────────────────────────────────────────────────────────────

export const CONCLUSION_FAMILY_LABELS: Record<ConclusionFamily, string> = {
  performance: 'Performance',
  condition: 'Condition',
  risk: 'Risk',
};

export const ELIGIBILITY_LABELS: Record<EligibilityState, string> = {
  blocked: 'Blocked',
  qualified: 'Qualified',
  defensible: 'Defensible',
};

export const ELIGIBILITY_COLORS: Record<EligibilityState, string> = {
  blocked: '#dc2626',    // red-600
  qualified: '#d97706',  // amber-600
  defensible: '#16a34a', // green-600
};
